const STORAGE_KEY_PREFIX = 'food_delivery_addresses'
const MAX_RECENT_ADDRESSES = 10

export { MAX_RECENT_ADDRESSES }

function getStorageKey(userId) {
  return userId ? `${STORAGE_KEY_PREFIX}:user:${userId}` : `${STORAGE_KEY_PREFIX}:guest`
}

export function buildAddressKey({ title, location } = {}) {
  const normalizedTitle = String(title ?? '').trim().toLowerCase()
  const lat = location?.lat != null ? Number(location.lat).toFixed(6) : ''
  const lng = location?.lng != null ? Number(location.lng).toFixed(6) : ''
  return `${normalizedTitle}|${lat}|${lng}`
}

function normalizeStoredEntry(raw) {
  if (!raw || typeof raw !== 'object') {
    return null
  }

  const title = String(raw.title ?? '').trim()
  if (!title) {
    return null
  }

  const location = raw.location
  const lat = location?.lat != null ? Number(location.lat) : NaN
  const lng = location?.lng != null ? Number(location.lng) : NaN
  const hasLocation = Number.isFinite(lat) && Number.isFinite(lng)

  const entry = {
    id: String(raw.id ?? buildAddressKey({ title, location: raw.location })),
    title,
    subtitle: String(raw.subtitle ?? '').trim(),
    location: hasLocation ? { lat, lng } : null,
    deliverable: typeof raw.deliverable === 'boolean' ? raw.deliverable : null,
    usedAt: raw.usedAt ?? null,
  }

  entry.id = buildAddressKey(entry)
  return entry
}

export function readDeliveryAddressStorage(userId) {
  if (typeof localStorage === 'undefined') {
    return { current: null, recent: [] }
  }

  try {
    const raw = localStorage.getItem(getStorageKey(userId))
    if (!raw) {
      return { current: null, recent: [] }
    }

    const parsed = JSON.parse(raw)
    const current = normalizeStoredEntry(parsed?.current)
    const recent = Array.isArray(parsed?.recent)
      ? parsed.recent.map(normalizeStoredEntry).filter(Boolean)
      : []

    return { current, recent }
  } catch {
    return { current: null, recent: [] }
  }
}

export function writeDeliveryAddressStorage(userId, { current, recent }) {
  if (typeof localStorage === 'undefined') {
    return
  }

  const payload = {
    current: current ? normalizeStoredEntry(current) : null,
    recent: Array.isArray(recent)
      ? recent.map(normalizeStoredEntry).filter(Boolean).slice(0, MAX_RECENT_ADDRESSES)
      : [],
  }

  localStorage.setItem(getStorageKey(userId), JSON.stringify(payload))
}

export function createAddressEntry({ title, location, subtitle = '', deliverable = null }) {
  const entry = normalizeStoredEntry({
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
    title,
    subtitle,
    location,
    deliverable: typeof deliverable === 'boolean' ? deliverable : null,
    usedAt: Date.now(),
  })

  if (entry) {
    entry.id = buildAddressKey(entry)
  }

  return entry
}
